import React, { useEffect, useRef } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import api from '../services/api';

const ATTACHMENT_URL_RE = /\/api\/support-desk\/tickets\/[^"'\s]+\/attachments\/[^"'\s]+\/download/;

interface Props {
  html: string;
  className?: string;
  sx?: SxProps<Theme>;
}

/**
 * Renders email HTML (e.g. a support ticket's customer reply) via dangerouslySetInnerHTML,
 * then swaps any <img> pointing at our ticket-attachment download endpoint to a blob: URL.
 *
 * Needed because that endpoint requires a Bearer token the browser's plain <img> tag can't
 * send — poller.py rewrites inline cid: image references to this URL, but it 401s unless
 * fetched through the authenticated API client first.
 */
const EmailHtmlContent: React.FC<Props> = ({ html, className, sx }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const blobUrlsRef = useRef<string[]>([]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    blobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    blobUrlsRef.current = [];

    let cancelled = false;
    const imgs = Array.from(container.querySelectorAll('img[src]')) as HTMLImageElement[];
    imgs.forEach((img) => {
      const src = img.getAttribute('src') || '';
      if (!ATTACHMENT_URL_RE.test(src)) return;
      api.fetchDocumentBlobUrl(src)
        .then((blobUrl) => {
          if (cancelled) {
            URL.revokeObjectURL(blobUrl);
            return;
          }
          blobUrlsRef.current.push(blobUrl);
          img.src = blobUrl;
        })
        .catch(() => { /* leave the broken-image icon if the fetch fails */ });
    });

    return () => { cancelled = true; };
  }, [html]);

  useEffect(() => () => {
    blobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
  }, []);

  return (
    <Box
      ref={containerRef}
      className={className}
      sx={sx}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default EmailHtmlContent;
